import React from 'react';
import FloridaCMEMap from '../charts/FloridaCMEMap';

const CMEScholarshipSection = () => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <h2 className="text-3xl font-bold text-gray-900">Pediatric Primary Care Practitioner</h2>
      <h3 className="text-2xl font-semibold text-blue-600 mt-2">5 CME Scholarship Request</h3>
      <p className="text-lg text-gray-700 mt-2">Boston Children's Hospital Building Bridges of Understanding</p>
    </div>

    {/* Hero Images Section */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="w-full h-40 overflow-hidden rounded-lg">
          <img 
            src="/src/images/adolescent-wellness.png" 
            alt="Adolescent Wellness" 
            className="w-full h-full object-contain bg-gray-50"
          />
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="w-full h-40 overflow-hidden rounded-lg">
          <img 
            src="/src/images/unnamed.jpg" 
            alt="Building Bridges Program" 
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>

    {/* Course Information */}
    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
      <h3 className="text-xl font-semibold text-blue-900 mb-4">Course Information</h3>
      <p className="text-gray-700 mb-4">
        Complete the form below to request the full scholarship for the self-paced Building Bridges of Understanding course. 
        Upon course completion you will have earned:
      </p>
      <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
        <p className="text-gray-700 mb-3">
          This course is designed to provide you with the knowledge and skills necessary to effectively support adolescents in a primary care setting.
        </p>
        <div className="flex items-center gap-2">
          <a 
            href="https://docs.google.com/forms/d/e/1FAIpQLSd5muRbcEP_CYjJLNutFaLPgaIfpGNQ1cZ6rpFmDRQah09X1g/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            Request for Scholarship
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>5 AMA PRA Category 1 Credits™</strong> (MD, DO, NP, PA)</li>
        <li><strong>Contact Hours</strong> (Nurse, Nurse Practitioner)</li>
        <li><strong>AAPA Category 1</strong> (Physician Assistant)</li>
      </ul>
      <p className="text-gray-700 mb-4">
        You can then download the digital <strong>Clinical Manual for Pediatric Practitioners</strong>.
      </p>
      <p className="text-gray-700">
        By completing this request, you will receive the Boston Children's Hospital scholarship URL to register and access the course.
      </p>
    </div>

    {/* Partnership Information */}
    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
      <h3 className="text-lg font-semibold text-green-900 mb-3">Partnership & Support</h3>
      <p className="text-gray-700 mb-3">
        This scholarship is provided through a collaboration of:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-3 bg-white rounded-lg">
          <div className="font-semibold text-green-800">Rotary Clubs</div>
          <div className="text-sm text-gray-600">Community Service</div>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <div className="font-semibold text-green-800">Adolescent Wellness, Inc.</div>
          <div className="text-sm text-gray-600">Mental Health Advocacy</div>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <div className="font-semibold text-green-800">Florida Association of Community Health Centers</div>
          <div className="text-sm text-gray-600">Primary Care Network</div>
        </div>
      </div>
    </div>

    {/* Contact Information */}
    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
      <p className="text-gray-700">
        <strong>For questions:</strong> Please call Bob Anthony at <span className="font-semibold text-blue-600">(781) 727-8617</span> or 
        email <span className="font-semibold text-blue-600">rwa1645@gmail.com</span>
      </p>
    </div>

    {/* Florida CME Course Analytics Map */}
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Florida CME Course Registration Analytics</h3>
      <p className="text-gray-600 mb-4">
        Geographic distribution of Building Bridges course registrations across Florida healthcare organizations
      </p>
      <FloridaCMEMap />
    </div>

    {/* Registration Breakdown by County */}
    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
      <h3 className="text-xl font-semibold text-blue-900 mb-4">Registration Breakdown by County</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-medium text-blue-800 mb-3">Florida Counties:</h4>
          <ul className="space-y-2">
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Broward:</span>
              <span className="font-semibold text-blue-600">1 registrant</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Collier:</span>
              <span className="font-semibold text-blue-600">25 registrants</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Hillsborough:</span>
              <span className="font-semibold text-blue-600">1 registrant</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Lee:</span>
              <span className="font-semibold text-blue-600">16 registrants</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Leon:</span>
              <span className="font-semibold text-blue-600">1 registrant</span>
            </li>
          </ul>
        </div>
        
        <div>
          <h4 className="text-lg font-medium text-blue-800 mb-3">Out-of-State:</h4>
          <ul className="space-y-2">
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Middlesex (MA):</span>
              <span className="font-semibold text-blue-600">1 registrant</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-gray-700">St. Croix (VI):</span>
              <span className="font-semibold text-blue-600">2 registrants</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-gray-700">Worcester (MA):</span>
              <span className="font-semibold text-blue-600">1 registrant</span>
            </li>
          </ul>
          
          <div className="mt-4 pt-3 border-t border-blue-300">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-blue-900">Total:</span>
              <span className="text-lg font-bold text-blue-700">46 registrants</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Additional Resources */}
    <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
      <h3 className="text-lg font-semibold text-purple-900 mb-3">Additional Resources</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-purple-800 mb-2">Course Benefits</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• Self-paced learning format</li>
            <li>• Evidence-based content</li>
            <li>• Expert faculty instruction</li>
            <li>• Digital clinical manual included</li>
          </ul>
        </div>
        <div>
          <h4 className="font-medium text-purple-800 mb-2">Program Impact</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• Enhanced diagnostic skills</li>
            <li>• Improved patient outcomes</li>
            <li>• Better care coordination</li>
            <li>• Reduced referral wait times</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

export default CMEScholarshipSection;
